import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/services/api';

interface Alert {
    id: number;
    type: 'CONFLICT' | 'CRITICAL_NODE' | 'LEVEL_III_OPERATION' | 'SYSTEM' | 'INFO';
    title: string;
    message: string;
    severity: 'HIGH' | 'MEDIUM' | 'LOW' | 'INFO';
    status: 'PENDING' | 'ACKNOWLEDGED' | 'RESOLVED' | 'DISMISSED';
    relatedOperation?: string;
    targetAgencies: string[];
    createdAt: string;
    acknowledgedAt?: string;
    resolvedAt?: string;
    acknowledgedBy?: string;
}

const typeLabels = {
    CONFLICT: { labelKey: 'admin.alerts.types.CONFLICT', icon: '⚠️', color: 'text-yellow-400' },
    CRITICAL_NODE: { labelKey: 'admin.alerts.types.CRITICAL_NODE', icon: '🔴', color: 'text-red-400' },
    LEVEL_III_OPERATION: { labelKey: 'admin.alerts.types.LEVEL_III_OPERATION', icon: '🚨', color: 'text-orange-400' },
    SYSTEM: { labelKey: 'admin.alerts.types.SYSTEM', icon: '💻', color: 'text-blue-400' },
    INFO: { labelKey: 'admin.alerts.types.INFO', icon: 'ℹ️', color: 'text-gray-400' },
};

const severityLabels = {
    HIGH: { labelKey: 'admin.alerts.severities.HIGH', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
    MEDIUM: { labelKey: 'admin.alerts.severities.MEDIUM', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    LOW: { labelKey: 'admin.alerts.severities.LOW', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    INFO: { labelKey: 'admin.alerts.severities.INFO', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
};

const statusLabels = {
    PENDING: { labelKey: 'admin.alerts.statuses.PENDING', color: 'bg-yellow-500/20 text-yellow-400' },
    ACKNOWLEDGED: { labelKey: 'admin.alerts.statuses.ACKNOWLEDGED', color: 'bg-blue-500/20 text-blue-400' },
    RESOLVED: { labelKey: 'admin.alerts.statuses.RESOLVED', color: 'bg-green-500/20 text-green-400' },
    DISMISSED: { labelKey: 'admin.alerts.statuses.DISMISSED', color: 'bg-gray-500/20 text-gray-400' },
};

// Datos de demostración
const demoAlerts: Alert[] = [
    { id: 1, type: 'CONFLICT', title: 'Conflicto de Operaciones Detectado', message: 'Las operaciones CAR-2024-087 y PDI-2024-052 se superponen en Sector Centro, comuna de Santiago. Se recomienda coordinación entre las unidades.', severity: 'HIGH', status: 'PENDING', relatedOperation: 'CAR-2024-087 / PDI-2024-052', targetAgencies: ['CAR', 'PDI'], createdAt: '2024-12-12T10:30:00' },
    { id: 2, type: 'CRITICAL_NODE', title: 'Nudo Crítico NC-2024-015 Creado', message: 'Se ha identificado un nudo crítico con 3 operaciones en conflicto en la zona de Las Condes. Nivel de riesgo: Alto.', severity: 'HIGH', status: 'ACKNOWLEDGED', relatedOperation: 'NC-2024-015', targetAgencies: ['CAR', 'PDI', 'GOPE'], createdAt: '2024-12-12T09:15:00', acknowledgedAt: '2024-12-12T09:20:00', acknowledgedBy: 'María Fernández' },
    { id: 3, type: 'LEVEL_III_OPERATION', title: 'Nueva Operación Nivel III', message: 'Se ha registrado una nueva operación clasificada como Nivel III (Alta Complejidad): "Operativo Desarticulación Red Nacional". Requiere supervisión especial.', severity: 'MEDIUM', status: 'PENDING', relatedOperation: 'PDI-2024-078', targetAgencies: ['PDI', 'ADMIN'], createdAt: '2024-12-12T08:45:00' },
    { id: 4, type: 'CONFLICT', title: 'Superposición Temporal Detectada', message: 'Operación GOPE-2024-023 tiene conflicto temporal con OS9-2024-018 en el mismo sector. Coordinar horarios.', severity: 'MEDIUM', status: 'RESOLVED', relatedOperation: 'GOPE-2024-023 / OS9-2024-018', targetAgencies: ['GOPE', 'OS9'], createdAt: '2024-12-11T16:30:00', resolvedAt: '2024-12-11T17:45:00' },
    { id: 5, type: 'SYSTEM', title: 'Mantenimiento Programado', message: 'El sistema VANTAK estará en mantenimiento el día 15 de diciembre de 02:00 a 05:00 hrs. Algunas funciones pueden no estar disponibles.', severity: 'INFO', status: 'PENDING', targetAgencies: ['ALL'], createdAt: '2024-12-10T14:00:00' },
    { id: 6, type: 'INFO', title: 'Nueva Institución Agregada', message: 'Se ha agregado la institución "Dirección de Seguridad y Colaboración" al sistema. Ya pueden coordinar operaciones.', severity: 'LOW', status: 'DISMISSED', targetAgencies: ['ALL'], createdAt: '2024-12-09T11:00:00' },
];

export default function AlertsPage() {
    const { t } = useTranslation();
    const [alerts, setAlerts] = useState<Alert[]>([]);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState<string>('');
    const [filterStatus, setFilterStatus] = useState<string>('');
    const [filterSeverity, setFilterSeverity] = useState<string>('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);

    const [newAlert, setNewAlert] = useState({
        type: 'INFO' as Alert['type'],
        title: '',
        message: '',
        severity: 'INFO' as Alert['severity'],
        targetAgencies: [] as string[],
    });

    useEffect(() => {
        loadAlerts();
    }, []);

    const loadAlerts = async () => {
        try {
            const response = await api.get('/api/v1/alerts');
            setAlerts(response.data);
        } catch {
            setAlerts(demoAlerts);
        } finally {
            setLoading(false);
        }
    };

    const handleAcknowledge = async (alertId: number) => {
        try {
            await api.put(`/api/v1/alerts/${alertId}/acknowledge`);
            loadAlerts();
        } catch {
            setAlerts(alerts.map(a => a.id === alertId ? { ...a, status: 'ACKNOWLEDGED' as const, acknowledgedAt: new Date().toISOString() } : a));
        }
    };

    const handleResolve = async (alertId: number) => {
        try {
            await api.put(`/api/v1/alerts/${alertId}/resolve`);
            loadAlerts();
        } catch {
            setAlerts(alerts.map(a => a.id === alertId ? { ...a, status: 'RESOLVED' as const, resolvedAt: new Date().toISOString() } : a));
        }
    };

    const handleDismiss = async (alertId: number) => {
        try {
            await api.put(`/api/v1/alerts/${alertId}/dismiss`);
            loadAlerts();
        } catch {
            setAlerts(alerts.map(a => a.id === alertId ? { ...a, status: 'DISMISSED' as const } : a));
        }
    };

    const handleCreateAlert = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await api.post('/api/v1/alerts', newAlert);
            loadAlerts();
        } catch {
            const alert: Alert = {
                id: alerts.length + 1,
                ...newAlert,
                status: 'PENDING',
                createdAt: new Date().toISOString(),
            };
            setAlerts([alert, ...alerts]);
        }
        setShowCreateModal(false);
        setNewAlert({ type: 'INFO', title: '', message: '', severity: 'INFO', targetAgencies: [] });
    };

    const filteredAlerts = alerts.filter(alert => {
        const matchesType = !filterType || alert.type === filterType;
        const matchesStatus = !filterStatus || alert.status === filterStatus;
        const matchesSeverity = !filterSeverity || alert.severity === filterSeverity;
        return matchesType && matchesStatus && matchesSeverity;
    });

    const pendingCount = alerts.filter(a => a.status === 'PENDING').length;
    const highPriorityCount = alerts.filter(a => a.severity === 'HIGH' && a.status === 'PENDING').length;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-white">{t('admin.alerts.title')}</h1>
                    <p className="text-gray-400 mt-1">{t('admin.alerts.subtitle')}</p>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                    <span>📢</span>
                    {t('admin.alerts.newNotification')}
                </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4">
                <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
                    <div className="text-2xl font-bold text-white">{alerts.length}</div>
                    <div className="text-gray-400 text-sm">{t('admin.alerts.stats.total')}</div>
                </div>
                <div className="bg-yellow-500/10 rounded-xl p-4 border border-yellow-500/30">
                    <div className="text-2xl font-bold text-yellow-400">{pendingCount}</div>
                    <div className="text-gray-400 text-sm">{t('admin.alerts.stats.pending')}</div>
                </div>
                <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
                    <div className="text-2xl font-bold text-red-400">{highPriorityCount}</div>
                    <div className="text-gray-400 text-sm">{t('admin.alerts.stats.highPriority')}</div>
                </div>
                <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
                    <div className="text-2xl font-bold text-green-400">{alerts.filter(a => a.status === 'RESOLVED').length}</div>
                    <div className="text-gray-400 text-sm">{t('admin.alerts.stats.resolved')}</div>
                </div>
            </div>

            {/* Filters */}
            <div className="flex gap-4">
                <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2 text-white"
                >
                    <option value="">{t('admin.alerts.filters.allTypes')}</option>
                    <option value="CONFLICT">{t('admin.alerts.filterTypes.CONFLICT')}</option>
                    <option value="CRITICAL_NODE">{t('admin.alerts.types.CRITICAL_NODE')}</option>
                    <option value="LEVEL_III_OPERATION">{t('admin.alerts.types.LEVEL_III_OPERATION')}</option>
                    <option value="SYSTEM">{t('admin.alerts.types.SYSTEM')}</option>
                    <option value="INFO">{t('admin.alerts.types.INFO')}</option>
                </select>
                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2 text-white"
                >
                    <option value="">{t('admin.alerts.filters.allStatuses')}</option>
                    <option value="PENDING">{t('admin.alerts.statuses.PENDING')}</option>
                    <option value="ACKNOWLEDGED">{t('admin.alerts.statuses.ACKNOWLEDGED')}</option>
                    <option value="RESOLVED">{t('admin.alerts.statuses.RESOLVED')}</option>
                    <option value="DISMISSED">{t('admin.alerts.statuses.DISMISSED')}</option>
                </select>
                <select
                    value={filterSeverity}
                    onChange={(e) => setFilterSeverity(e.target.value)}
                    className="bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2 text-white"
                >
                    <option value="">{t('admin.alerts.filters.allPriorities')}</option>
                    <option value="HIGH">{t('admin.alerts.severities.HIGH')}</option>
                    <option value="MEDIUM">{t('admin.alerts.severities.MEDIUM')}</option>
                    <option value="LOW">{t('admin.alerts.severities.LOW')}</option>
                    <option value="INFO">{t('admin.alerts.severities.INFO')}</option>
                </select>
            </div>

            {/* Alerts List */}
            <div className="space-y-3">
                {filteredAlerts.map(alert => (
                    <div
                        key={alert.id}
                        className={`bg-gray-800/50 rounded-xl p-5 border transition-colors cursor-pointer
                            ${alert.status === 'PENDING' && alert.severity === 'HIGH' ? 'border-red-500/50 bg-red-500/5' :
                                alert.status === 'PENDING' ? 'border-yellow-500/30' : 'border-gray-700/50'}
                        `}
                        onClick={() => setSelectedAlert(alert)}
                    >
                        <div className="flex items-start gap-4">
                            <div className={`text-3xl ${typeLabels[alert.type].color}`}>
                                {typeLabels[alert.type].icon}
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center gap-3">
                                    <h3 className="text-lg font-semibold text-white">{alert.title}</h3>
                                    <span className={`px-2 py-0.5 rounded text-xs border ${severityLabels[alert.severity].color}`}>
                                        {t(severityLabels[alert.severity].labelKey)}
                                    </span>
                                    <span className={`px-2 py-0.5 rounded text-xs ${statusLabels[alert.status].color}`}>
                                        {t(statusLabels[alert.status].labelKey)}
                                    </span>
                                </div>
                                <p className="text-gray-400 mt-1 line-clamp-2">{alert.message}</p>
                                <div className="flex items-center gap-4 mt-3 text-sm">
                                    <span className="text-gray-500">
                                        {new Date(alert.createdAt).toLocaleString('es-CL')}
                                    </span>
                                    {alert.relatedOperation && (
                                        <span className="text-blue-400">📋 {alert.relatedOperation}</span>
                                    )}
                                    <span className="text-gray-500">
                                        {t('admin.alerts.recipients')}: {alert.targetAgencies.join(', ')}
                                    </span>
                                </div>
                            </div>
                            {alert.status === 'PENDING' && (
                                <div className="flex gap-2">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleAcknowledge(alert.id); }}
                                        className="bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 px-3 py-1 rounded-lg text-sm"
                                    >
                                        ✓ {t('admin.alerts.actions.acknowledge')}
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleResolve(alert.id); }}
                                        className="bg-green-600/20 hover:bg-green-600/40 text-green-400 px-3 py-1 rounded-lg text-sm"
                                    >
                                        ✓ {t('admin.alerts.actions.resolve')}
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleDismiss(alert.id); }}
                                        className="bg-gray-600/20 hover:bg-gray-600/40 text-gray-400 px-3 py-1 rounded-lg text-sm"
                                    >
                                        ✕
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Alert Detail Modal */}
            {selectedAlert && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedAlert(null)}>
                    <div className="bg-gray-800 rounded-xl p-6 w-full max-w-2xl border border-gray-700" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-4 mb-4">
                            <span className={`text-4xl ${typeLabels[selectedAlert.type].color}`}>
                                {typeLabels[selectedAlert.type].icon}
                            </span>
                            <div>
                                <h2 className="text-xl font-bold text-white">{selectedAlert.title}</h2>
                                <div className="flex gap-2 mt-1">
                                    <span className={`px-2 py-0.5 rounded text-xs border ${severityLabels[selectedAlert.severity].color}`}>
                                        {t(severityLabels[selectedAlert.severity].labelKey)}
                                    </span>
                                    <span className={`px-2 py-0.5 rounded text-xs ${statusLabels[selectedAlert.status].color}`}>
                                        {t(statusLabels[selectedAlert.status].labelKey)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-700/30 rounded-lg p-4 mb-4">
                            <p className="text-gray-300">{selectedAlert.message}</p>
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-gray-400">{t('admin.alerts.detail.type')}:</span>
                                <span className="text-white ml-2">{t(typeLabels[selectedAlert.type].labelKey)}</span>
                            </div>
                            <div>
                                <span className="text-gray-400">{t('admin.alerts.detail.created')}:</span>
                                <span className="text-white ml-2">{new Date(selectedAlert.createdAt).toLocaleString('es-CL')}</span>
                            </div>
                            {selectedAlert.relatedOperation && (
                                <div>
                                    <span className="text-gray-400">{t('admin.alerts.detail.operation')}:</span>
                                    <span className="text-blue-400 ml-2">{selectedAlert.relatedOperation}</span>
                                </div>
                            )}
                            <div>
                                <span className="text-gray-400">{t('admin.alerts.recipients')}:</span>
                                <span className="text-white ml-2">{selectedAlert.targetAgencies.join(', ')}</span>
                            </div>
                            {selectedAlert.acknowledgedAt && (
                                <div>
                                    <span className="text-gray-400">{t('admin.alerts.detail.acknowledgedBy')}:</span>
                                    <span className="text-white ml-2">{selectedAlert.acknowledgedBy} ({new Date(selectedAlert.acknowledgedAt).toLocaleString('es-CL')})</span>
                                </div>
                            )}
                            {selectedAlert.resolvedAt && (
                                <div>
                                    <span className="text-gray-400">{t('admin.alerts.detail.resolvedAt')}:</span>
                                    <span className="text-green-400 ml-2">{new Date(selectedAlert.resolvedAt).toLocaleString('es-CL')}</span>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-700">
                            <button
                                onClick={() => setSelectedAlert(null)}
                                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                            >
                                {t('admin.common.close')}
                            </button>
                            {selectedAlert.status === 'PENDING' && (
                                <>
                                    <button
                                        onClick={() => { handleResolve(selectedAlert.id); setSelectedAlert(null); }}
                                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
                                    >
                                        {t('admin.alerts.markAsResolved')}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Create Alert Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-gray-800 rounded-xl p-6 w-full max-w-lg border border-gray-700">
                        <h2 className="text-xl font-bold text-white mb-4">📢 {t('admin.alerts.newNotification')}</h2>
                        <form onSubmit={handleCreateAlert} className="space-y-4">
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">{t('admin.alerts.form.title')}</label>
                                <input
                                    type="text"
                                    value={newAlert.title}
                                    onChange={(e) => setNewAlert({ ...newAlert, title: e.target.value })}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">{t('admin.alerts.form.message')}</label>
                                <textarea
                                    value={newAlert.message}
                                    onChange={(e) => setNewAlert({ ...newAlert, message: e.target.value })}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white h-24"
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">{t('admin.alerts.form.type')}</label>
                                    <select
                                        value={newAlert.type}
                                        onChange={(e) => setNewAlert({ ...newAlert, type: e.target.value as Alert['type'] })}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                                    >
                                        <option value="INFO">{t('admin.alerts.types.INFO')}</option>
                                        <option value="SYSTEM">{t('admin.alerts.types.SYSTEM')}</option>
                                        <option value="LEVEL_III_OPERATION">{t('admin.alerts.types.LEVEL_III_OPERATION')}</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">{t('admin.alerts.form.priority')}</label>
                                    <select
                                        value={newAlert.severity}
                                        onChange={(e) => setNewAlert({ ...newAlert, severity: e.target.value as Alert['severity'] })}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                                    >
                                        <option value="INFO">{t('admin.alerts.severities.INFO')}</option>
                                        <option value="LOW">{t('admin.alerts.severities.LOW')}</option>
                                        <option value="MEDIUM">{t('admin.alerts.severities.MEDIUM')}</option>
                                        <option value="HIGH">{t('admin.alerts.severities.HIGH')}</option>
                                    </select>
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowCreateModal(false)}
                                    className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                                >
                                    {t('admin.common.cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
                                >
                                    {t('admin.alerts.sendNotification')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
